import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';
import { getTeamLogoUrl } from '../../utils/logoUrl';
import { formatDate, formatDateOnly, formatTimeOnly } from '../../utils/dateUtils';

interface Team {
  id: string;
  name: string;
  shortName?: string;
  logoUrl?: string;
  competition?: {
    id: string;
    name: string;
  };
}

interface Season {
  id: string;
  name: string;
  isActive?: boolean;
  competition?: {
    id: string;
    name: string;
  };
}

interface MatchGroup {
  id: string;
  name: string;
  season: {
    id: string;
    name: string;
  };
}

interface MatchResultHistoryEntry {
  id: string;
  homeGoals: number;
  awayGoals: number;
  createdAt: string;
  setBy: {
    id: string;
    teamName: string;
    firstName?: string;
    lastName?: string;
  };
}

interface Match {
  id: string;
  homeTeam: Team;
  awayTeam: Team;
  season: Season;
  matchGroup: MatchGroup;
  matchDate: string;
  predictionsClose: string;
  venue?: string;
  homeGoals?: number;
  awayGoals?: number;
  status: 'scheduled' | 'in_progress' | 'finished' | 'cancelled';
  resultsSet?: string;
  matchWeek?: number;
  resultHistory?: MatchResultHistoryEntry[];
}

const formatAdminUserName = (user: MatchResultHistoryEntry['setBy']): string => {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return fullName || user.teamName;
};

const matchHasExistingResult = (match: Match): boolean =>
  match.resultsSet != null ||
  (match.status === 'finished' && match.homeGoals != null && match.awayGoals != null);

const formatFilterDate = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DEFAULT_RANGE_DAYS = 14;

const getMatchDateRange = (days: number = DEFAULT_RANGE_DAYS) => {
  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const to = new Date(from);
  to.setUTCDate(to.getUTCDate() + days);
  return {
    dateFrom: formatFilterDate(from),
    dateTo: formatFilterDate(to),
  };
};

const getDefaultMatchDateRange = () => getMatchDateRange(DEFAULT_RANGE_DAYS);

const hasActiveSearchFilters = (
  team: string,
  season: string,
  status: string,
  matchGroupId: string
): boolean =>
  team.trim() !== '' ||
  season !== 'all' ||
  status !== '' ||
  matchGroupId !== '';

const AdminMatches: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [matchGroups, setMatchGroups] = useState<MatchGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSeason, setSelectedSeason] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>(() => getDefaultMatchDateRange().dateFrom);
  const [dateTo, setDateTo] = useState<string>(() => getDefaultMatchDateRange().dateTo);
  const [datesManuallySet, setDatesManuallySet] = useState(false);
  const [searchTeam, setSearchTeam] = useState<string>('');
  const [debouncedSearchTeam, setDebouncedSearchTeam] = useState<string>('');
  const [selectedMatchGroupId, setSelectedMatchGroupId] = useState<string>('');
  const [showResultModal, setShowResultModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMatch, setEditingMatch] = useState<Match | null>(null);
  const [resultForm, setResultForm] = useState({
    homeGoals: '',
    awayGoals: ''
  });
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [createForm, setCreateForm] = useState({
    season: '',
    matchGroup: '',
    homeTeam: '',
    awayTeam: '',
    matchDate: '',
    predictionsClose: '',
    venue: '',
    matchWeek: ''
  });
  const [editForm, setEditForm] = useState({
    matchDate: '',
    predictionsClose: '',
    matchGroup: '',
    venue: '',
    matchWeek: '',
    status: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [queueStatus, setQueueStatus] = useState<{
    queueLength: number;
    processing: boolean;
    processedCount: number;
    errorCount: number;
  } | null>(null);
  const [pollingQueue, setPollingQueue] = useState(false);
  const fetchRequestIdRef = useRef(0);

  useEffect(() => {
    fetchSeasons();
    fetchTeams();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearchTeam(searchTeam), 300);
    return () => clearTimeout(timeoutId);
  }, [searchTeam]);

  useEffect(() => {
    fetchMatches();
  }, [selectedSeason, selectedStatus, debouncedSearchTeam, selectedMatchGroupId, dateFrom, dateTo, datesManuallySet]);

  useEffect(() => {
    if (datesManuallySet) return;

    if (hasActiveSearchFilters(debouncedSearchTeam, selectedSeason, selectedStatus, selectedMatchGroupId)) {
      setDateFrom('');
      setDateTo('');
      return;
    }

    const range = getDefaultMatchDateRange();
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
  }, [debouncedSearchTeam, selectedSeason, selectedStatus, selectedMatchGroupId, datesManuallySet]);

  const matchGroupOptions = useMemo(() => {
    const seen = new Set<string>();
    const withCompetition = matches
      .map((m) => {
        const competitionLabel = m.season.competition?.name ?? m.season.name;
        return { matchGroup: m.matchGroup, competitionLabel };
      })
      .filter(({ matchGroup }) => {
        if (seen.has(matchGroup.id)) return false;
        seen.add(matchGroup.id);
        return true;
      })
      .map(({ matchGroup, competitionLabel }) => ({
        id: matchGroup.id,
        name: matchGroup.name,
        label: `${matchGroup.name} (${competitionLabel})`
      }));
    return withCompetition.sort((a, b) => {
      const numA = parseInt(a.name.match(/\d+$/)?.[0] ?? '', 10);
      const numB = parseInt(b.name.match(/\d+$/)?.[0] ?? '', 10);
      if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
      return a.name.localeCompare(b.name);
    });
  }, [matches]);

  useEffect(() => {
    const handleSeasonChange = async () => {
      if (selectedSeason) {
        console.log(`Season changed to: ${selectedSeason}`);
        
        // First fetch match groups for the new season
        await fetchMatchGroups();
        
        // Then update form after match groups are loaded
        setCreateForm(prevForm => {
          console.log(`Updating createForm.season from ${prevForm.season} to ${selectedSeason}`);
          return {
            ...prevForm,
            season: selectedSeason,
            matchGroup: '', // Clear match group when season changes
          };
        });
      }
    };
    
    handleSeasonChange();
  }, [selectedSeason]);

  // Watch for changes to the form season (when user changes season in the modal)
  useEffect(() => {
    const handleFormSeasonChange = async () => {
      if (createForm.season && createForm.season !== selectedSeason) {
        console.log(`Form season changed to: ${createForm.season} (different from filter season: ${selectedSeason})`);
        
        // Fetch match groups for the form's selected season
        try {
          console.log('Clearing match groups state for form season change...');
          setMatchGroups([]);
          
          console.log(`Fetching match groups for form season: ${createForm.season}`);
          const response = await apiClient.get(`/admin/matchgroups?season=${createForm.season}`);
          
          console.log(`API returned ${response.data.length} match groups for form season ${createForm.season}`);
          console.log('Form season match groups:', response.data.map((mg: MatchGroup) => ({ 
            id: mg.id, 
            name: mg.name, 
            season: mg.season.id,
            seasonName: mg.season.name 
          })));
          
          setMatchGroups(response.data);
        } catch (err: any) {
          console.error('Failed to fetch match groups for form season:', err);
          setMatchGroups([]);
        }
      }
    };
    
    handleFormSeasonChange();
  }, [createForm.season, selectedSeason]);

  const fetchSeasons = async () => {
    try {
      const response = await apiClient.get('/admin/seasons');
      setSeasons(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load seasons');
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await apiClient.get('/admin/teams');
      setTeams(response.data);
    } catch (err: any) {
      console.error('Failed to fetch teams:', err);
    }
  };

  const fetchMatchGroups = async () => {
    try {
      // Always clear match groups first
      console.log('Clearing match groups state...');
      setMatchGroups([]);
      
      if (!selectedSeason) {
        console.log('No season selected, keeping match groups empty');
        return;
      }
      
      console.log(`Fetching match groups for season: ${selectedSeason}`);
      const response = await apiClient.get(`/admin/matchgroups?season=${selectedSeason}`);
      
      console.log(`API returned ${response.data.length} match groups for season ${selectedSeason}`);
      console.log('Match groups data:', response.data.map((mg: MatchGroup) => ({ 
        id: mg.id, 
        name: mg.name, 
        season: mg.season.id,
        seasonName: mg.season.name 
      })));
      
      setMatchGroups(response.data);
    } catch (err: any) {
      console.error('Failed to fetch match groups:', err);
      setMatchGroups([]); // Clear on error
    }
  };

  const fetchMatches = async () => {
    const requestId = ++fetchRequestIdRef.current;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedSeason && selectedSeason !== 'all') {
        params.append('season', selectedSeason);
      } else if (debouncedSearchTeam.trim()) {
        params.append('activeOnly', 'true');
      }
      if (selectedStatus) params.append('status', selectedStatus);
      if (debouncedSearchTeam.trim()) params.append('team', debouncedSearchTeam.trim());
      if (selectedMatchGroupId) params.append('matchGroup', selectedMatchGroupId);

      const searchFiltersActive = hasActiveSearchFilters(
        debouncedSearchTeam,
        selectedSeason,
        selectedStatus,
        selectedMatchGroupId
      );
      const shouldApplyDates = datesManuallySet || !searchFiltersActive;

      if (shouldApplyDates) {
        const range = getDefaultMatchDateRange();
        let from = dateFrom;
        let to = dateTo;

        if (datesManuallySet) {
          if (!from.trim()) from = range.dateFrom;
          if (!to.trim()) to = range.dateTo;
        } else {
          from = range.dateFrom;
          to = range.dateTo;
        }

        if (from.trim() !== '') params.append('dateFrom', from);
        if (to.trim() !== '') params.append('dateTo', to);
      }

      const response = await apiClient.get(`/admin/matches?${params.toString()}`);
      if (requestId !== fetchRequestIdRef.current) return;

      setMatches(response.data);
    } catch (err: any) {
      if (requestId !== fetchRequestIdRef.current) return;
      setError(err.response?.data?.message || 'Failed to load matches');
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleCreateMatch = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Validation
      if (createForm.homeTeam === createForm.awayTeam) {
        setError('Home and away teams cannot be the same');
        return;
      }

      if (new Date(createForm.predictionsClose) >= new Date(createForm.matchDate)) {
        setError('Predictions must close before the match starts');
        return;
      }

      // Ensure match group belongs to selected season
      const selectedMatchGroup = matchGroups.find(mg => mg.id === createForm.matchGroup);
      if (createForm.matchGroup && selectedMatchGroup && selectedMatchGroup.season.id !== createForm.season) {
        setError('Selected match group does not belong to the current season. Please select a different match group.');
        return;
      }

      // Convert datetime-local strings to UTC for database storage
      const matchDateUTC = new Date(createForm.matchDate + ':00.000Z');
      const predictionsCloseUTC = new Date(createForm.predictionsClose + ':00.000Z');
      
      const matchData = {
        ...createForm,
        matchDate: matchDateUTC.toISOString(),
        predictionsClose: predictionsCloseUTC.toISOString(),
        matchWeek: createForm.matchWeek ? parseInt(createForm.matchWeek) : undefined
      };

      await apiClient.post('/admin/matches', matchData);
      setShowCreateModal(false);
      resetCreateForm();
      fetchMatches();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create match');
    } finally {
      setSubmitting(false);
    }
  };

  const openResultModal = (match: Match) => {
    setEditingMatch(match);
    setResultForm({
      homeGoals: match.homeGoals?.toString() || '',
      awayGoals: match.awayGoals?.toString() || ''
    });
    setConfirmOverwrite(false);
    setShowResultModal(true);
  };

  const closeResultModal = () => {
    setShowResultModal(false);
    setEditingMatch(null);
    setResultForm({ homeGoals: '', awayGoals: '' });
    setConfirmOverwrite(false);
  };

  const openEditModal = async (match: Match) => {
    setEditingMatch(match);
    
    // Fetch match groups for this match's season if needed
    if (match.season.id !== selectedSeason) {
      try {
        console.log(`Fetching match groups for editing match's season: ${match.season.id}`);
        const response = await apiClient.get(`/admin/matchgroups?season=${match.season.id}`);
        console.log(`Loaded ${response.data.length} match groups for season ${match.season.name}`);
        setMatchGroups(response.data);
      } catch (err: any) {
        console.error('Failed to fetch match groups for edit modal:', err);
      }
    } else {
      // If it's the same season as the filter, make sure we have the latest match groups
      await fetchMatchGroups();
    }
    
    // Format UTC times directly for datetime-local inputs (no timezone conversion)
    const formatForDateTimeLocal = (dateString: string) => {
      const date = new Date(dateString);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    setEditForm({
      matchDate: formatForDateTimeLocal(match.matchDate),
      predictionsClose: formatForDateTimeLocal(match.predictionsClose),
      matchGroup: match.matchGroup.id,
      venue: match.venue || '',
      matchWeek: match.matchWeek?.toString() || '',
      status: match.status
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingMatch(null);
    setEditForm({
      matchDate: '',
      predictionsClose: '',
      matchGroup: '',
      venue: '',
      matchWeek: '',
      status: ''
    });
  };

  const resetCreateForm = () => {
    setCreateForm({
      season: selectedSeason,
      matchGroup: '',
      homeTeam: '',
      awayTeam: '',
      matchDate: '',
      predictionsClose: '',
      venue: '',
      matchWeek: ''
    });
  };

  const openCreateModal = async () => {
    // Ensure we have the latest season and match groups
    if (selectedSeason) {
      console.log('Modal opening: Fetching latest match groups...');
      await fetchMatchGroups();
    }
    
    resetCreateForm();
    setShowCreateModal(true);
    
    // Debug logging - wait a moment for state to update
    setTimeout(() => {
      console.log('Opening create modal with:', {
        selectedSeason,
        matchGroupsCount: matchGroups.length,
        matchGroups: matchGroups.map(mg => ({ id: mg.id, name: mg.name, season: mg.season.id }))
      });
    }, 100);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    resetCreateForm();
  };

  const fetchQueueStatus = async () => {
    try {
      const response = await apiClient.get('/admin/leaderboard-queue-status');
      setQueueStatus(response.data);
      return response.data;
    } catch (err) {
      console.error('Error fetching queue status:', err);
      return null;
    }
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (pollingQueue) {
      // Poll immediately, then every 2 seconds
      fetchQueueStatus();
      intervalId = setInterval(async () => {
        const status = await fetchQueueStatus();
        // Stop polling if queue is empty and not processing
        if (status && status.queueLength === 0 && !status.processing) {
          setPollingQueue(false);
        }
      }, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [pollingQueue]);

  const updateMatchResult = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMatch) return;

    if (matchHasExistingResult(editingMatch) && !confirmOverwrite) {
      setConfirmOverwrite(true);
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post(`/admin/matches/${editingMatch.id}/result`, {
        homeGoals: parseInt(resultForm.homeGoals),
        awayGoals: parseInt(resultForm.awayGoals)
      });
      
      closeResultModal();
      fetchMatches();
      
      // Start polling queue status after successful submission
      setPollingQueue(true);
      await fetchQueueStatus();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update match result');
    } finally {
      setSubmitting(false);
    }
  };

  const renderResultHistory = (
    history: MatchResultHistoryEntry[] | undefined,
    variant: 'table' | 'modal'
  ) => {
    if (!history?.length) return null;

    const containerClass = variant === 'table'
      ? 'mt-2 space-y-1 max-w-xs'
      : 'mt-4 p-3 rounded-md bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 space-y-2';

    const entries = variant === 'table' ? history.slice(0, 3) : history;

    return (
      <div className={containerClass}>
        {variant === 'modal' && (
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Result history
          </p>
        )}
        {entries.map((entry) => (
          <div
            key={entry.id}
            className={`text-xs text-gray-500 dark:text-gray-400 ${variant === 'modal' ? 'flex flex-wrap gap-x-2 gap-y-1' : ''}`}
          >
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {entry.homeGoals} - {entry.awayGoals}
            </span>
            <span>{formatDate(entry.createdAt)}</span>
            <span>by {formatAdminUserName(entry.setBy)}</span>
          </div>
        ))}
        {variant === 'table' && history.length > 3 && (
          <div className="text-xs text-gray-400 dark:text-gray-500">
            +{history.length - 3} more in result modal
          </div>
        )}
      </div>
    );
  };

  const handleEditMatch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMatch) return;

    setSubmitting(true);
    
    try {
      // Validation
      if (new Date(editForm.predictionsClose) >= new Date(editForm.matchDate)) {
        setError('Predictions must close before the match starts');
        return;
      }

      // Convert datetime-local strings to UTC for database storage
      const matchDateUTC = new Date(editForm.matchDate + ':00.000Z');
      const predictionsCloseUTC = new Date(editForm.predictionsClose + ':00.000Z');
      
      const matchData = {
        matchDate: matchDateUTC.toISOString(),
        predictionsClose: predictionsCloseUTC.toISOString(),
        matchGroupId: editForm.matchGroup,
        venue: editForm.venue,
        matchWeek: editForm.matchWeek ? parseInt(editForm.matchWeek) : undefined,
        status: editForm.status
      };

      await apiClient.put(`/admin/matches/${editingMatch.id}`, matchData);
      closeEditModal();
      fetchMatches();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update match');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteMatch = async (matchId: string) => {
    if (!window.confirm('Are you sure you want to delete this match?')) {
      return;
    }

    try {
      await apiClient.delete(`/admin/matches/${matchId}`);
      fetchMatches();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete match');
    }
  };



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'text-blue-600';
      case 'in_progress': return 'text-yellow-600';
      case 'finished': return 'text-green-600';
      case 'cancelled': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading matches...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Manage Matches
          </h1>
          <Link 
            to="/admin" 
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            ← Back to Admin Dashboard
          </Link>
        </div>
        <button
          onClick={openCreateModal}
          className="btn-primary px-4 py-2"
          disabled={!selectedSeason}
        >
          Add New Match
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button 
            onClick={() => setError('')}
            className="float-right text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}

      {/* Leaderboard Update Queue Status */}
      {queueStatus && (queueStatus.queueLength > 0 || queueStatus.processing) && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200 px-4 py-3 rounded mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-800 dark:border-blue-200"></div>
              <span className="font-medium">
                Updating leaderboards...
              </span>
              <span className="text-sm">
                {queueStatus.processing ? (
                  <>Processing... ({queueStatus.queueLength} remaining)</>
                ) : (
                  <>{queueStatus.queueLength} in queue</>
                )}
              </span>
            </div>
            {queueStatus.processedCount > 0 && (
              <span className="text-sm">
                {queueStatus.processedCount} processed
                {queueStatus.errorCount > 0 && (
                  <span className="text-red-600 dark:text-red-400 ml-2">
                    ({queueStatus.errorCount} errors)
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Filters
          </h3>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const today = formatFilterDate(new Date());
                setSelectedSeason('all');
                setSelectedStatus('');
                setSearchTeam('');
                setDebouncedSearchTeam('');
                setDatesManuallySet(true);
                setDateFrom(today);
                setDateTo(today);
                setSelectedMatchGroupId('');
              }}
              className="text-sm px-3 py-1 border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              style={{ color: 'var(--text-primary)' }}
            >
              Show Today's Matches
            </button>
            <button
              onClick={() => {
                setSelectedSeason('all');
                setSelectedStatus('');
                setSearchTeam('');
                setDebouncedSearchTeam('');
                setSelectedMatchGroupId('');
                setDatesManuallySet(false);
              }}
              className="text-sm px-3 py-1 border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              style={{ color: 'var(--text-primary)' }}
            >
              Reset filters
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Search team
            </label>
            <input
              type="text"
              placeholder="Home or away team name..."
              value={searchTeam}
              onChange={(e) => setSearchTeam(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Match group (week)
            </label>
            <select
              value={selectedMatchGroupId}
              onChange={(e) => setSelectedMatchGroupId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
            >
              <option value="">All groups</option>
              {matchGroupOptions.map((mg) => (
                <option key={mg.id} value={mg.id}>
                  {mg.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Season
            </label>
            <select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
            >
              <option value="all">All Seasons</option>
              {seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name} {season.competition && `(${season.competition.name})`}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
            >
              <option value="">All Statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="finished">Finished</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Date range
            </label>
            <div className="flex items-center w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 focus-within:ring-2 focus-within:ring-highlight focus-within:border-highlight overflow-hidden">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDatesManuallySet(true);
                  setDateFrom(e.target.value);
                }}
                aria-label="From date"
                className="flex-1 min-w-0 px-2 py-2 bg-transparent text-gray-900 dark:text-white border-0 focus:ring-0 text-sm"
              />
              <span className="text-gray-400 dark:text-gray-500 text-sm shrink-0">–</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDatesManuallySet(true);
                  setDateTo(e.target.value);
                }}
                aria-label="To date"
                className="flex-1 min-w-0 px-2 py-2 bg-transparent text-gray-900 dark:text-white border-0 focus:ring-0 text-sm"
              />
            </div>
            {!dateFrom && !dateTo && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                All dates (set a range to narrow results)
              </p>
            )}
            <button
              type="button"
              onClick={() => {
                setDatesManuallySet(true);
                const range = getDefaultMatchDateRange();
                setDateFrom(range.dateFrom);
                setDateTo(range.dateTo);
              }}
              className="mt-2 text-sm text-highlight hover:underline"
            >
              Show next 14 days
            </button>
          </div>
        </div>
      </div>

            {/* Matches Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Match
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Season/Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Result
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {matches.map((match) => (
                <tr key={match.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            {match.homeTeam.logoUrl && (
                              <img 
                                src={getTeamLogoUrl(match.homeTeam.logoUrl)}
                                alt={`${match.homeTeam.name} logo`}
                                className="w-5 h-5 object-contain"
                              />
                            )}
                            <span>{match.homeTeam.name}</span>
                          </div>
                          <span>vs</span>
                          <div className="flex items-center gap-1">
                            <span>{match.awayTeam.name}</span>
                            {match.awayTeam.logoUrl && (
                              <img 
                                src={getTeamLogoUrl(match.awayTeam.logoUrl)}
                                alt={`${match.awayTeam.name} logo`}
                                className="w-5 h-5 object-contain"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
{/*                     {match.venue && (
                      <div className="text-sm text-gray-500 dark:text-gray-300">@ {match.venue}</div>
                    )} */}
                   {/*  {match.matchWeek && (
                      <div className="text-xs text-gray-400 dark:text-gray-400">Week {match.matchWeek}</div>
                    )} */}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {formatDateOnly(match.matchDate)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-300">
                      {formatTimeOnly(match.matchDate)}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-400">
                      Predictions close: {formatTimeOnly(match.predictionsClose)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{match.season.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-300">{match.matchGroup.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      match.status === 'finished' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                      match.status === 'in_progress' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                      match.status === 'cancelled' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                      'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                    }`}>
                      {match.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {match.homeGoals !== undefined && match.awayGoals !== undefined ? (
                      <span className="font-medium">
                        {match.homeGoals} - {match.awayGoals}
                      </span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-400">-</span>
                    )}
                    {renderResultHistory(match.resultHistory, 'table')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">

                      <button
                        onClick={() => openResultModal(match)}
                        className="btn-primary px-3 py-1 text-sm"
                      >
                        Set Result
                      </button>

                      <button
                        onClick={() => openEditModal(match)}
                        className="btn-secondary px-3 py-1 text-sm"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => deleteMatch(match.id)}
                        className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {matches.length === 0 && (
            <div className="text-center py-8">
              <p className="text-xl text-gray-500">
                No matches found. Add your first match or adjust your filters.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create Match Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card p-6 max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Add New Match
            </h2>
            
            <form onSubmit={handleCreateMatch}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Season *
                  </label>
                  <select
                    value={createForm.season}
                    onChange={(e) => {
                      console.log(`User changed form season to: ${e.target.value}`);
                      setCreateForm({ ...createForm, season: e.target.value, matchGroup: '' });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    required
                  >
                    <option value="">Select a season...</option>
                    {seasons.map((season) => (
                      <option key={season.id} value={season.id}>
                        {season.name} {season.competition && `(${season.competition.name})`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Match Group *
                  </label>
                  <select
                    value={createForm.matchGroup}
                    onChange={(e) => setCreateForm({ ...createForm, matchGroup: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    required
                  >
                    <option value="">Select a match group...</option>
                    {(() => {
                      const currentSeason = createForm.season || selectedSeason;
                      const filteredGroups = matchGroups.filter(group => group.season.id === currentSeason);
                      console.log(`Filtering match groups:`, {
                        currentSeason,
                        totalGroups: matchGroups.length,
                        filteredCount: filteredGroups.length,
                        allGroups: matchGroups.map(mg => ({ id: mg.id, name: mg.name, season: mg.season.id })),
                        filteredGroups: filteredGroups.map(mg => ({ id: mg.id, name: mg.name }))
                      });
                      return filteredGroups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ));
                    })()}
                  </select>
                  {matchGroups.length === 0 && (createForm.season || selectedSeason) && (
                    <p className="text-sm text-red-600 mt-1">
                      No match groups found for this season. Please create match groups first.
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Home Team *
                  </label>
                  <select
                    value={createForm.homeTeam}
                    onChange={(e) => setCreateForm({ ...createForm, homeTeam: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    required
                  >
                    <option value="">Select home team...</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name} {team.shortName ? `(${team.shortName})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Away Team *
                  </label>
                  <select
                    value={createForm.awayTeam}
                    onChange={(e) => setCreateForm({ ...createForm, awayTeam: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    required
                  >
                    <option value="">Select away team...</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id} disabled={team.id === createForm.homeTeam}>
                        {team.name} {team.shortName ? `(${team.shortName})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Match Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={createForm.matchDate}
                    onChange={(e) => setCreateForm({ ...createForm, matchDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Predictions Close *
                  </label>
                  <input
                    type="datetime-local"
                    value={createForm.predictionsClose}
                    onChange={(e) => setCreateForm({ ...createForm, predictionsClose: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Venue
                  </label>
                  <input
                    type="text"
                    value={createForm.venue}
                    onChange={(e) => setCreateForm({ ...createForm, venue: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    placeholder="e.g., Old Trafford"
                    maxLength={100}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Match Week (Deprecated)
                  </label>
                  <input
                    type="number"
                    value={createForm.matchWeek}
                    onChange={(e) => setCreateForm({ ...createForm, matchWeek: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    placeholder="e.g., 1, 2, 3..."
                    min="1"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={submitting}
                >
                  {submitting ? 'Creating...' : 'Create Match'}
                </button>
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="btn-secondary flex-1"
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Result Modal */}
      {showResultModal && editingMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card p-6 max-w-md w-full mx-4 max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Set Match Result
            </h2>
            
            <div className="text-center mb-4">
              <p className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
                {editingMatch.homeTeam.name} vs {editingMatch.awayTeam.name}
              </p>
              <p className="text-sm text-gray-600">
                {formatDateOnly(editingMatch.matchDate)}
              </p>
            </div>

            {matchHasExistingResult(editingMatch) && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded text-sm text-amber-900 dark:text-amber-200">
                This match already has a result set ({editingMatch.homeGoals} - {editingMatch.awayGoals}).
                Saving again will overwrite it and re-score all predictions.
              </div>
            )}

            {renderResultHistory(editingMatch.resultHistory, 'modal')}
            
            <form onSubmit={updateMatchResult}>
              <div className="grid grid-cols-2 gap-4 mb-6 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    {editingMatch.homeTeam.name} Goals
                  </label>
                  <input
                    type="number"
                    value={resultForm.homeGoals}
                    onChange={(e) => {
                      setConfirmOverwrite(false);
                      setResultForm({ ...resultForm, homeGoals: e.target.value });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight text-center"
                    min="0"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    {editingMatch.awayTeam.name} Goals
                  </label>
                  <input
                    type="number"
                    value={resultForm.awayGoals}
                    onChange={(e) => {
                      setConfirmOverwrite(false);
                      setResultForm({ ...resultForm, awayGoals: e.target.value });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight text-center"
                    min="0"
                    required
                  />
                </div>
              </div>

              {confirmOverwrite && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded text-sm text-red-900 dark:text-red-200">
                  <p className="font-medium mb-1">Are you sure you want to overwrite this result?</p>
                  <p>
                    This will replace the current score and re-score every prediction for this match.
                  </p>
                </div>
              )}

              {queueStatus && (queueStatus.queueLength > 0 || queueStatus.processing) && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded text-sm text-blue-800 dark:text-blue-200">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-800 dark:border-blue-200"></div>
                    <span>
                      Leaderboard updates are being processed in the background. 
                      {queueStatus.queueLength > 0 && ` ${queueStatus.queueLength} remaining.`}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="submit"
                  className={`flex-1 ${confirmOverwrite ? 'bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded' : 'btn-primary'}`}
                  disabled={submitting}
                >
                  {submitting
                    ? 'Saving...'
                    : confirmOverwrite
                      ? 'Yes, overwrite result'
                      : 'Save Result'}
                </button>
                {confirmOverwrite ? (
                  <button
                    type="button"
                    onClick={() => setConfirmOverwrite(false)}
                    className="btn-secondary flex-1"
                    disabled={submitting}
                  >
                    Go Back
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={closeResultModal}
                    className="btn-secondary flex-1"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Match Modal */}
      {showEditModal && editingMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card p-6 max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Edit Match
            </h2>
            
            <div className="text-center mb-4">
              <p className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
                {editingMatch.homeTeam.name} vs {editingMatch.awayTeam.name}
              </p>
              <p className="text-sm text-gray-600">
                {editingMatch.season.name} - {editingMatch.matchGroup.name}
              </p>
            </div>
            
            <form onSubmit={handleEditMatch}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Match Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={editForm.matchDate}
                    onChange={(e) => setEditForm({ ...editForm, matchDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Predictions Close *
                  </label>
                  <input
                    type="datetime-local"
                    value={editForm.predictionsClose}
                    onChange={(e) => setEditForm({ ...editForm, predictionsClose: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Match Group *
                  </label>
                  <select
                    value={editForm.matchGroup}
                    onChange={(e) => setEditForm({ ...editForm, matchGroup: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    required
                  >
                    <option value="">Select a match group...</option>
                    {matchGroups
                      .filter(group => editingMatch && group.season.id === editingMatch.season.id)
                      .map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name} ({group.season.name})
                        </option>
                      ))}
                  </select>
                  {editingMatch && matchGroups.filter(g => g.season.id === editingMatch.season.id).length === 0 && (
                    <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                      No match groups found for {editingMatch.season.name}. Please create match groups first.
                    </p>
                  )}
                </div>


                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Status *
                  </label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    required
                  >
                    <option value="scheduled">Scheduled</option>
                    <option value="in_progress">In Progress</option>
                    <option value="finished">Finished</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Venue
                  </label>
                  <input
                    type="text"
                    value={editForm.venue}
                    onChange={(e) => setEditForm({ ...editForm, venue: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    placeholder="Stadium name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Match Week (Deprecated)
                  </label>
                  <input
                    type="number"
                    value={editForm.matchWeek}
                    onChange={(e) => setEditForm({ ...editForm, matchWeek: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    placeholder="Week number"
                    min="1"
                  />
                </div>


              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="btn-secondary flex-1"
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMatches; 